import { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { GQLEnumPostStatusEnum } from "@framework/__generated__/graphql/enums";
import { ContentAdsContainer_contentResource$key } from "@framework/__generated__/relay/ContentAdsContainer_contentResource.graphql";
import useRouteInfo from "@framework/route/use-route-info";
import { useConfigContext } from "@voxmedia/duet-context/context/config-context";
import { getSeoTitle } from "@voxmedia/duet-utils/seo";

import ConcertAds from "./ConcertAds";

interface Props {
  contentResource: ContentAdsContainer_contentResource$key;
  boostedPostEnabled?: boolean;
  boostedContentResource?: ContentAdsContainer_contentResource$key;
  excludedContainers?: string[];
  isMobileAppRequest?: boolean;
}

const ContentAdsContainer: FunctionComponent<Props> = (props) => {
  const {
    boostedPostEnabled,
    boostedContentResource,
    excludedContainers,
    isMobileAppRequest,
  } = props;

  const node = useFragment(
    /**
     * @excludeCacheKey: This is a utility fragment that includes shared fields from the parent
     * query. Decisions around cache key inclusion should be made in the main layout query or UI
     * components where the data directly affects page content.
     *
     * https://duet-docs.voxops.net/graphql/custom-directives/index.html#1-utility-fragments
     */
    graphql`
      fragment ContentAdsContainer_contentResource on ContentResourceInterface {
        __typename
        id
        globalId
        chorusId
        status
        resourceType
        title
        permalink
        originalPublishedAt
        dek {
          plaintext
        }
        seo {
          headline
        }
        primaryCampaign {
          title
          advertiserName
        }

        ... on FeedContentResourceInterface {
          excludedFromHomepage
          # @excludeCacheKey: assignment/unassignment of authors will result in invalidation of the
          # resource cache key, so including the key is not needed
          authors @excludeCacheKey {
            name
          }
          # @excludeCacheKey: assignment/unassignment of primary category will result in invalidation
          # of the resource cache key, so including the key is not needed
          primaryCategory @excludeCacheKey {
            parent @excludeCacheKey {
              slug
            }
            slug
            isHub
          }
          # @excludeCacheKey: assignment/unassignment of categories will result in invalidation
          # of the resource cache key, so including the key is not needed
          internalSuperCats @excludeCacheKey {
            slug
          }
          # @excludeCacheKey: assignment/unassignment of categories will result in invalidation
          # of the resource cache key, so including the key is not needed
          superCats @excludeCacheKey {
            slug
          }
          # @excludeCacheKey: assignment/unassignment of categories will result in invalidation
          # of the resource cache key, so including the key is not needed
          internalCategories @excludeCacheKey {
            slug
          }
          # @excludeCacheKey: assignment/unassignment of categories will result in invalidation
          # of the resource cache key, so including the key is not needed
          categories @excludeCacheKey {
            slug
            isHub
          }
          packages(first: 100) {
            nodes {
              globalId
            }
          }

          ... on PostResourceType {
            template
          }
        }
      }
    `,
    props.contentResource,
  );

  const boostedNode = useFragment(
    graphql`
      fragment ContentAdsContainer_boostedContentResource on ContentResourceInterface {
        __typename
        id
        globalId
        chorusId
        ... on FeedContentResourceInterface {
          categories @excludeCacheKey {
            slug
          }
          internalCategories @excludeCacheKey {
            slug
          }
          superCats @excludeCacheKey {
            slug
          }
          internalSuperCats @excludeCacheKey {
            slug
          }
        }
      }
    `,
    boostedContentResource ?? null,
  );

  const route = useRouteInfo();
  const isPaginating = route.page && route.page > 1;
  const packageIds: string[] = [];

  if (node.__typename === "PackageResourceType") {
    packageIds.push(node.globalId);
  }
  if ("packages" in node && node.packages) {
    packageIds.push(...node.packages.nodes.map(({ globalId }) => globalId));
  }

  // pagination/ archives is used on stream landing
  const getArchivesTitle = (title: string | null) =>
    title && isPaginating ? `${title} - Page ${route.page}` : title;

  const adSlugParts = [];
  const { communityName } = useConfigContext().communityConfig;

  if (
    node.__typename !== "PostResourceType" &&
    node.__typename !== "StreamResourceType" &&
    node.__typename !== "PackageResourceType" &&
    node.__typename !== "PageResourceType" &&
    node.__typename !== "VideoGameResourceType" &&
    node.__typename !== "VideoPostResourceType" &&
    node.__typename !== "QuickPostResourceType" &&
    node.__typename !== "VenueResourceType" &&
    node.__typename !== "MapResourceType" &&
    node.__typename !== "CustomStoryResourceType" &&
    node.__typename !== "EmbedStoryResourceType"
  ) {
    return null;
  }

  const authors: string[] = Array.isArray(node.authors)
    ? node.authors.map(({ name }: { name: string }) => name).filter(Boolean)
    : [];

  const categories: string[] = Array.isArray(node.categories)
    ? node.categories
        .map((category: { slug: string }) => category.slug)
        .filter(Boolean)
    : [];

  if (Array.isArray(node.internalCategories)) {
    categories.push(
      ...node.internalCategories.map((ic: { slug: string }) => ic.slug),
    );
  }

  if (Array.isArray(node.superCats)) {
    categories.push(...node.superCats.map((ic: { slug: string }) => ic.slug));
  }

  if (Array.isArray(node.internalSuperCats)) {
    categories.push(
      ...node.internalSuperCats.map((ic: { slug: string }) => ic.slug),
    );
  }

  const boostedPostCategories: string[] = [];
  if (
    boostedNode &&
    "__typename" in boostedNode &&
    boostedNode.__typename !== "PageResourceType"
  ) {
    if (Array.isArray(boostedNode.categories)) {
      boostedPostCategories.push(
        ...boostedNode.categories.map((c: { slug: string }) => c.slug),
      );
    }
    if (Array.isArray(boostedNode.internalCategories)) {
      boostedPostCategories.push(
        ...boostedNode.internalCategories.map((c: { slug: string }) => c.slug),
      );
    }
    if (Array.isArray(boostedNode.superCats)) {
      boostedPostCategories.push(
        ...boostedNode.superCats.map((c: { slug: string }) => c.slug),
      );
    }
    if (Array.isArray(boostedNode.internalSuperCats)) {
      boostedPostCategories.push(
        ...boostedNode.internalSuperCats.map((c: { slug: string }) => c.slug),
      );
    }
  }

  const hubPages: string[] = Array.isArray(node.categories)
    ? node.categories
        .filter((category: { isHub: boolean; slug: string }) => category.isHub)
        .map((category: { slug: string }) => category.slug)
        .filter(Boolean)
    : [];

  // If node has primary category add category slug to ad slug parts
  if (
    node.primaryCategory?.slug &&
    node.primaryCategory.slug !== "uncategorized"
  ) {
    // If node has primary category and it has a parent category add category slug to ad slug parts
    if (node.primaryCategory.parent?.slug) {
      adSlugParts.push(node.primaryCategory.parent.slug);
    }

    // Add primary category to slug parts.
    adSlugParts.push(node.primaryCategory.slug);
  }

  if (isPaginating) {
    adSlugParts.push("archives");
  }

  return (
    <ConcertAds
      adSlugParts={adSlugParts}
      globalId={node.globalId}
      chorusId={node.chorusId}
      packageIds={packageIds}
      permalink={node.permalink}
      title={node.title}
      seoHeadline={getSeoTitle(
        [getArchivesTitle(node.seo.headline)],
        communityName,
      )}
      authors={authors}
      categories={categories}
      hubPages={hubPages}
      dek={node.dek.plaintext}
      originalPublishedAt={node.originalPublishedAt}
      resourceType={node.resourceType}
      template={"template" in node ? node.template : null}
      campaign={node.primaryCampaign}
      // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison -- GraphQL string status vs enum
      preview={node.status !== GQLEnumPostStatusEnum.publish}
      pageType={isPaginating ? "archive_page" : "interior_page"}
      boostedPostEnabled={boostedPostEnabled}
      boostedPostGlobalId={boostedNode?.globalId}
      boostedPostChorusId={boostedNode?.chorusId}
      boostedPostCategories={
        boostedPostCategories.length > 0 ? boostedPostCategories : undefined
      }
      excludedContainers={excludedContainers}
      isMobileAppRequest={isMobileAppRequest}
    />
  );
};
export default ContentAdsContainer;
